let expandableId = 0;

/**
 * Expandable text component with line clamping and smooth height transitions.
 * 
 * @package AGDS
 * @category Modules
 * @module Expandable
 * @see AGDSApp
 * @author Andy Groove
 * @since 1.0.0
 * @typedef {Object} ExpandableOptions
 * @property {Element|NodeListOf<Element>|HTMLCollectionOf<Element>} [elements]
 */
export default class Expandable {
    /**
     * @type {WeakMap<Element, { destroy: () => void }>}
     */
    #instances = new WeakMap();

    /**
     * @param {ExpandableOptions} [options={}]
     */
    constructor(options = {}) {
        this.elements = this.#normalizeElements(options?.elements);
    }

    /**
     * Initialize all expandable instances.
     *
     * @returns {this}
     */
    init() {
        this.elements.forEach((container) => {
            this.initInstance(container);
        });

        return this;
    }

    /**
     * Initialize a single expandable instance.
     *
     * Repeated initialization of the same element is ignored.
     *
     * @param {Element} container
     * @returns {this}
     */
    initInstance(container) {
        if (!(container instanceof Element) || this.#instances.has(container)) {
            return this;
        }

        const content = container.querySelector("[data-expandable-content]");
        const button = container.querySelector("[data-expandable-btn]");

        if (!(content instanceof HTMLElement) || !(button instanceof HTMLElement)) {
            return this;
        }

        const maxLines = this.#getMaxLines(container);
        const labelElement = button.querySelector("[data-expandable-label], span");

        const textMore =
            button.dataset.textMore?.trim() || labelElement?.textContent?.trim() || button.textContent.trim();

        const textLess = button.dataset.textLess?.trim() || textMore;

        const contentId = content.id || `expandable-content-${++expandableId}`;

        content.id = contentId;

        button.setAttribute("aria-controls", contentId);

        let fullHeight = 0;
        let collapsedHeight = 0;
        let isExpanded = container.classList.contains("_opened");
        let animationFrame = null;
        let lastWidth = null;

        /**
         * Update the visible button label without destroying its markup.
         *
         * @param {string} text
         */
        const setButtonText = (text) => {
            if (labelElement) {
                labelElement.textContent = text;
                return;
            }

            button.textContent = text;
        };

        /**
         * Update button and expandable state.
         */
        const updateState = () => {
            container.classList.toggle("_opened", isExpanded);

            button.setAttribute("aria-expanded", String(isExpanded));

            setButtonText(isExpanded ? textLess : textMore);

            content.style.maxHeight = `${isExpanded ? fullHeight : collapsedHeight}px`;
        };

        /**
         * Measure full and collapsed content heights.
         */
        const measure = () => {
            const previousTransition = content.style.transition;

            // Disable transition while measuring to avoid animated values.
            content.style.transition = "none";
            content.style.maxHeight = "none";

            // Measure the full content height.
            content.classList.remove("line-clamp");
            fullHeight = Math.ceil(content.getBoundingClientRect().height);

            // Measure the collapsed content height.
            content.style.setProperty("--line-clamp", String(maxLines));
            content.classList.add("line-clamp");

            collapsedHeight = Math.ceil(content.getBoundingClientRect().height);

            content.classList.remove("line-clamp");
            content.style.transition = previousTransition;
        };

        /**
         * Recalculate dimensions and update the component state.
         */
        const refresh = () => {
            measure();

            const canExpand = fullHeight > collapsedHeight + 1;

            button.classList.toggle("_hidden", !canExpand);

            if (!canExpand) {
                isExpanded = false;

                container.classList.remove("_opened");
                button.setAttribute("aria-expanded", "false");
                setButtonText(textMore);

                content.style.maxHeight = "none";

                return;
            }

            updateState();
        };

        /**
         * Schedule a single refresh on the next animation frame.
         */
        const scheduleRefresh = () => {
            if (animationFrame !== null) {
                return;
            }

            animationFrame = requestAnimationFrame(() => {
                animationFrame = null;
                refresh();
            });
        };

        /**
         * Toggle expandable state.
         */
        const handleClick = () => {
            if (fullHeight <= collapsedHeight + 1) {
                return;
            }

            isExpanded = !isExpanded;

            updateState();
        };

        button.addEventListener("click", handleClick);

        /**
         * Refresh when the container width changes.
         *
         * Height changes caused by the max-height transition are ignored.
         */
        const resizeObserver =
            typeof ResizeObserver !== "undefined"
                ? new ResizeObserver(([entry]) => {
                      const width = entry.contentRect.width;

                      if (lastWidth === null || Math.abs(width - lastWidth) > 0.5) {
                          lastWidth = width;
                          scheduleRefresh();
                      }
                  })
                : null;

        resizeObserver?.observe(container);

        /**
         * Refresh when the content itself changes.
         */
        const mutationObserver =
            typeof MutationObserver !== "undefined"
                ? new MutationObserver((mutations) => {
                      const hasContentChanges = mutations.some(
                          (mutation) => mutation.type === "childList" || mutation.type === "characterData",
                      );

                      if (hasContentChanges) {
                          scheduleRefresh();
                      }
                  })
                : null;

        mutationObserver?.observe(content, {
            childList: true,
            characterData: true,
            subtree: true,
        });

        /**
         * Refresh after images or other resources inside the content load.
         */
        const handleLoad = () => {
            scheduleRefresh();
        };

        content.addEventListener("load", handleLoad, true);

        const destroy = () => {
            button.removeEventListener("click", handleClick);

            content.removeEventListener("load", handleLoad, true);

            resizeObserver?.disconnect();
            mutationObserver?.disconnect();

            if (animationFrame !== null) {
                cancelAnimationFrame(animationFrame);
            }

            content.classList.remove("line-clamp");
            content.style.removeProperty("--line-clamp");
            content.style.removeProperty("max-height");

            button.classList.remove("_hidden");
            button.removeAttribute("aria-expanded");
            button.removeAttribute("aria-controls");

            container.classList.remove("_opened");

            this.#instances.delete(container);
        };

        this.#instances.set(container, {
            destroy,
        });

        refresh();

        return this;
    }

    /**
     * Destroy initialized expandable instances.
     *
     * @param {Element} [container]
     * @returns {this}
     */
    destroy(container) {
        if (container instanceof Element) {
            this.#instances.get(container)?.destroy();

            return this;
        }

        this.elements.forEach((element) => {
            this.#instances.get(element)?.destroy();
        });

        return this;
    }

    /**
     * Normalize different element collection types into an array.
     *
     * @param {unknown} elements
     * @returns {Element[]}
     */
    #normalizeElements(elements) {
        if (elements instanceof Element) {
            return [elements];
        }

        if (elements instanceof NodeList || elements instanceof HTMLCollection || Array.isArray(elements)) {
            return Array.from(elements).filter((element) => element instanceof Element);
        }

        return [];
    }

    /**
     * Get a valid line-clamp value.
     *
     * @param {Element} container
     * @returns {number}
     */
    #getMaxLines(container) {
        const value = Number.parseInt(container.getAttribute("data-expandable") ?? "", 10);

        return Number.isFinite(value) && value > 0 ? value : 10;
    }
}
